import React, { createContext, useContext, useState, useEffect } from 'react'
import { api, authApi } from '../services/api'
import { selfHealingClient } from '../services/circuit-breaker'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_auth_token')
    const userToken = localStorage.getItem('auth_token')
    const token = adminToken || userToken
    
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      getCurrentUser()
    } else {
      setLoading(false)
    }
  }, [])

  const getCurrentUser = async () => {
    try {
      // Check if we have user data in localStorage
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        // If admin token exists, ensure user has isAdmin property
        const adminToken = localStorage.getItem('admin_auth_token');
        if (adminToken && !parsedUser.isAdmin) {
          parsedUser.isAdmin = true;
          localStorage.setItem('user_data', JSON.stringify(parsedUser));
        }
        setUser(parsedUser);
        setLoading(false);
        return;
      }
      
      // Try admin endpoint as fallback
      const response = await api.get('/admin/auth/me');
      const adminUser = {...response.data, userType: 'admin', isAdmin: true};
      setUser(adminUser);
      localStorage.setItem('user_data', JSON.stringify(adminUser));
    } catch (error) {
      console.error('Failed to get current user:', error)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      // Only try regular user login - no fallback to admin
      let response;
      let userData;

      // For user login, use authApi which has correct base URL
      console.log('🚀 Attempting customer login:', email);
      console.log('🔍 authApi baseURL:', authApi.defaults.baseURL);
      console.log('🌐 Current location:', window.location.href);

      response = await authApi.post('/auth/login', { email, password });
      console.log('📥 Customer login response:', response.data);
      const { token, user, requires_verification, verification_message, setup_mode, scraping_job_id, message, redirect } = response.data;
      userData = {...user, userType: 'user'};

      // Handle expired trial — no token returned
      if (user?.starterExpired) {
        return {
          success: false,
          error: message || 'Your 7-day free trial has expired. Please upgrade to a paid plan or contact hello@cognavia.ai to continue.'
        };
      }

      localStorage.setItem('auth_token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Handle verification requirement - return immediately without setting user
      if (requires_verification) {
        console.log('🔒 User requires verification - returning verification flag WITHOUT setting user');
        // DON'T set user or localStorage until verification is complete
        // Just store the token for verification API calls and return user data for OTP form
        return {
          success: true,
          requires_verification: true,
          verification_message: verification_message,
          userData: userData  // Return user data for OTP verification form
        };
      }

      // If user login succeeds without verification requirement
      console.log('✅ Customer login successful without verification requirement');
      setUser(userData);
      localStorage.setItem('user_data', JSON.stringify(userData));
      return {
        success: true,
        setup_mode,
        scraping_job_id,
        redirect,          // backend-chosen landing path (/dashboard | /agent-dashboard)
        user: userData,    // callers may inspect role / parentUserId
      };
    } catch (error) {
      console.error('Login failed:', error)
      
      // Handle different types of errors with user-friendly messages
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const detail = error.response.data?.detail;
        
        switch (status) {
          case 401:
            // Check if it's a specific email not found error
            if (detail && detail.includes('non-existent email')) {
              errorMessage = '❌ Account not found. This email address is not registered. Please check your email or sign up for a new account.';
            } else if (detail && (detail.includes('Invalid email') || detail.includes('Invalid credentials'))) {
              errorMessage = '❌ Invalid email or password. Please double-check your credentials and try again.';
            } else {
              errorMessage = '❌ Login failed. Please verify your email address and password are correct.';
            }
            break;
          case 403:
            errorMessage = detail || 'Your account has been deactivated. Please upgrade to Premium or contact hello@cognavia.ai to restore access.';
            break;
          case 404:
            errorMessage = '📧 Email address not found. Please check your email spelling or create a new account.';
            break;
          case 422:
            errorMessage = '⚠️ Invalid login information. Please check your email format and try again.';
            break;
          case 429:
            errorMessage = '⏳ Too many login attempts. Please wait a few minutes before trying again.';
            break;
          case 500:
            // Show more detailed error for development/debugging
            if (detail && detail !== 'Login failed. Please try again.') {
              errorMessage = '🛠️ Server error: ' + detail;
            } else {
              errorMessage = '🛠️ Server error. Please try again in a few minutes or contact support if this continues.';
            }
            break;
          default:
            if (detail) {
              if (detail.includes('email')) {
                errorMessage = '📧 Email-related error: ' + detail;
              } else {
                errorMessage = '❌ ' + detail;
              }
            } else {
              errorMessage = '❌ Login failed. Please try again.';
            }
        }
      } else if (error.request) {
        // Network error
        errorMessage = '🌐 Unable to connect to server. Please check your internet connection and try again.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = '🌐 Network connection failed. Please check your internet connection.';
      } else if (error.message === 'Login request timed out' || error.code === 'ECONNABORTED') {
        errorMessage = '⏰ Login request timed out. The server may be busy. Please try again.';
      } else if (error.message.includes('timeout')) {
        errorMessage = '⏰ Request timed out. Please check your connection and try again.';
      }
      
      return { 
        success: false, 
        error: errorMessage
      }
    }
  }

  // Separate admin login method
  const adminLogin = async (email, password) => {
    try {
      console.log('Attempting admin login...')
      
      // Call admin login endpoint using configured API instance
      const response = await api.post('/admin/auth/login', {
        email,
        password
      })

      const { access_token, user: userData } = response.data

      if (access_token && userData) {
        // Set admin token and user
        localStorage.setItem('admin_auth_token', access_token)
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
        const adminUser = { ...userData, isAdmin: true }
        setUser(adminUser)
        localStorage.setItem('user_data', JSON.stringify(adminUser))
        
        console.log('Admin login successful')
        return { success: true }
      } else {
        return { 
          success: false, 
          error: 'Admin login failed'
        }
      }
    } catch (error) {
      console.error('Admin login failed:', error)
      
      let errorMessage = 'Admin login failed. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        
        switch (status) {
          case 401:
            errorMessage = 'Invalid admin credentials. Please check your email and password.';
            break;
          case 403:
            errorMessage = 'Admin access denied. Contact system administrator.';
            break;
          default:
            errorMessage = detail || 'Admin authentication failed.';
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to admin server. Please try again.';
      }
      
      return { 
        success: false, 
        error: errorMessage
      }
    }
  }

  const adminRegister = async (formData) => {
    try {
      console.log('Attempting admin registration...')

      const response = await api.post('/admin/auth/register', {
        full_name: formData.fullName,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        department: formData.department,
        access_level: formData.accessLevel
      })

      console.log('Admin registration response:', response.data)

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Administrator account created successfully!'
        }
      } else {
        return {
          success: false,
          error: response.data.error || 'Registration failed'
        }
      }
    } catch (error) {
      console.error('Admin registration error:', error)

      let errorMessage = 'Registration failed. Please try again.'

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.response?.status === 409) {
        errorMessage = 'An administrator with this email already exists'
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid registration data. Please check your inputs.'
      }

      return {
        success: false,
        error: errorMessage
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('admin_auth_token')
    localStorage.removeItem('user_data')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
  }

  const value = {
    user,
    setUser,
    login,
    adminLogin,
    adminRegister,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}